import { forwardRef, type InputHTMLAttributes } from 'react';

import { Check, Minus } from 'lucide-react';

import { cx } from '@/lib/cx';

import styles from './Checkbox.module.css';

export type CheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label?: string;
  /**
   * Some but not all of a group is selected.
   *
   * Rendered as `aria-checked="mixed"` rather than a third visual state alone:
   * colour is never the only signal (plan 9.5).
   */
  indeterminate?: boolean;
};

/**
 * A real checkbox input, visually hidden and drawn with a sibling.
 *
 * Not a div with `role="checkbox"`, which is what the prototype uses. A real input
 * comes with keyboard handling, form participation, and label association already
 * working, all of which have to be rebuilt, usually incompletely, otherwise.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, indeterminate = false, checked, className, disabled, ...rest },
  ref,
) {
  return (
    <label className={cx(styles.root, disabled && styles.disabled, className)}>
      <input
        {...rest}
        ref={ref}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        aria-checked={indeterminate ? 'mixed' : checked}
        className={styles.input}
      />
      <span className={styles.box} aria-hidden="true">
        {indeterminate ? (
          <Minus size={12} strokeWidth={3} />
        ) : checked ? (
          <Check size={12} strokeWidth={3} />
        ) : null}
      </span>
      {label ? <span className={styles.label}>{label}</span> : null}
    </label>
  );
});
